import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, of, timeout } from 'rxjs';
import { API_BASE_URL } from '../../../shared/config/api.config';
import { ApiResponse } from '../../../shared/models/api-response.model';
import { AuthTokens } from '../../../shared/models/auth-user.model';
import { SessionService } from '../../../shared/services/session.service';
import { AccessTokenService } from '../../../shared/services/access-token.service';
import { SupervisionSessionService } from '../../../shared/services/supervision-session.service';

@Injectable({ providedIn: 'root' })
export class SessionRestoreService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly tokens = inject(AccessTokenService);
  private readonly supervision = inject(SupervisionSessionService);

  restore() {
    // /exchange es el único mecanismo de autenticación de esta pestaña. No se debe
    // restaurar en paralelo una cookie perteneciente a una sesión normal previa.
    if (window.location.pathname === '/auth/supervision') {
      return of(undefined);
    }
    const supervised = this.supervision.getActive();
    if (supervised) {
      const userId = this.readSubject(supervised.token);
      if (userId === null) {
        this.supervision.clear();
        return of(undefined);
      }
      this.session.iniciarSesionDesdeBackend({ userId, email: supervised.email, role: [] });
      return of(undefined);
    }
    // The OAuth callback sets HttpOnly cookies. Refresh validates them on the server;
    // only decode the token returned by that request, never one from the URL/storage.
    return this.http.post<ApiResponse<AuthTokens>>(`${API_BASE_URL}/auth/refresh`, {}, {
      withCredentials: true,
    }).pipe(
      timeout(10000),
      map(response => {
        if (response.state === false) throw new Error('Session rejected');
        const payload = response.data.access_token.split('.')[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const bytes = Uint8Array.from(atob(base64), character => character.charCodeAt(0));
        const claims = JSON.parse(new TextDecoder().decode(bytes));
        if (typeof claims.email !== 'string' || !claims.email.includes('@') ||
            !Number.isSafeInteger(Number(claims.sub)) || Number(claims.sub) <= 0 ||
            typeof claims.exp !== 'number' || claims.exp * 1000 <= Date.now()) {
          throw new Error('Invalid session response');
        }
        this.tokens.setToken(response.data.access_token);
        this.session.iniciarSesionDesdeBackend({
          userId: Number(claims.sub), email: claims.email, role: claims.roles ?? [],
        });
      }),
      // Visitors without cookies and expired sessions must still be able to open the site.
      catchError(() => {
        this.tokens.clear();
        return of(undefined);
      }),
    );
  }

  private readSubject(token: string): number | null {
    try {
      const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(new TextDecoder().decode(
        Uint8Array.from(atob(payload), character => character.charCodeAt(0)),
      ));
      const subject = Number(claims.sub ?? claims.userId ?? claims.accountId);
      return Number.isSafeInteger(subject) && subject > 0 ? subject : null;
    } catch {
      return null;
    }
  }
}
